'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '../image/viewport-image';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '../ui/button';

import { RxArrowTopRight } from 'react-icons/rx';
import { MdStarRate } from 'react-icons/md';
import { SectionLightbox } from './section-lightbox';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { Reveal } from '../ui/reveal';

export function Realizations({ realizations }: { realizations: TDynamicData[] }) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const { t } = useTranslations();

  return (
    <>
      {isOpen && <SectionLightbox isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />}
      <section
        className={cn(
          'custom-section',
          'w-full pb-12 pt-6 sm:pb-16 sm:pt-10 md:pb-20 lg:pb-24 lg:pt-12',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
          )}
        >
          <div className="flex w-full items-end justify-between">
            <Reveal>
              <Heading subtitle={t('text2330')} title={t('text2331')} />
            </Reveal>
          </div>
          <div className="grid w-full grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:gap-10 xl:gap-12">
            {realizations.map(realization => (
              <Reveal key={realization.id}>
                <div className="col-span-1 flex w-full items-start justify-start gap-6 sm:gap-8 lg:gap-10 xl:gap-12">
                  <div
                    onClick={handleOpenGallery(realization?.fields?.[2]?.images, 0)}
                    className="relative cursor-pointer"
                  >
                    <Button
                      aria-label={`${t('text3534')} — ${realization.name}`}
                      variant={'secondary'}
                      size={'icon'}
                      className="absolute right-2.5 top-2.5 z-[6]"
                    >
                      <RxArrowTopRight size={20} aria-hidden="true" />
                    </Button>
                    <Image
                      src={realization?.fields?.[2]?.images?.[0]?.url ?? '/images/placeholder.png'}
                      alt={resolveImageAlt(
                        realization?.fields?.[2]?.images?.[0],
                        realization?.name,
                      )}
                      width={0}
                      height={0}
                      className="h-[205px] w-full min-w-[150px] max-w-[150px] rounded-[16px] object-cover object-center sm:h-[220px] lg:h-[250px]"
                      sizes="150px"
                    />
                  </div>
                  <div className="flex w-full flex-col items-start justify-start gap-3">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, index) => (
                        <MdStarRate size={16} className="text-[#edb000]" key={index} />
                      ))}
                    </div>
                    <div className="flex flex-col gap-4">
                      <h3
                        data-ea-visual-tag="h6"
                        className="text-base font-medium tracking-tight sm:text-lg lg:text-xl xl:text-2xl"
                      >
                        {realization.name}
                      </h3>
                      <p className="text-xs font-light opacity-70 sm:text-sm">
                        {realization.fields[0]?.value}
                      </p>
                      <p className="text-sm font-semibold tracking-tight">
                        {realization.fields[1]?.value}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
